import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { EmployeeQuestionnaireStatusesEnum } from '@thinknet-gmbh/platform-common';

import { getFiltersCount } from 'src/common/helpers/common.helpers';
import { useDeclineEmployeeDocumentTemplate } from 'src/hooks/employee-questionnaire/decline-employee-questionnaire.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetOpenQuestionnairesForTable } from 'src/hooks/employee-questionnaire/get-employee-questionnaires.hook';

import { OpenQuestionnairesListTable } from './components/open-questionnaires-list-table';
import { OpenQuestionnairesTableFilter } from './components/open-questionnaires-table-filter';

import {
  INITIAL_QUESTIONNAIRE_DATA,
  QUESTIONNAIRE_MODALS,
} from './open-questionnaires-list.constants';

import { IFilterData, IOpenQuestionnaire } from './open-questionnaires-list.interfaces';
import { HeaderContainer } from '../employee-list/employee-list.styles';

const OpenQuestionnairesList = () => {
  const { t } = useTranslation(['employee', 'common']);
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const [currentEmployeeData, setCurrentEmployeeData] = useState<IOpenQuestionnaire>(
    INITIAL_QUESTIONNAIRE_DATA
  );
  const [filters, setFilters] = useState<IFilterData>({});
  const { member } = useGetCurrentMemberAccount();
  const currentCompanyId = useGetCurrentCompanyId();
  const { employees, paginationConfig, loading, onTableChange } = useGetOpenQuestionnairesForTable({
    searchParam: filters.searchParam || '',
    filter: {
      companyId: currentCompanyId,
      companyFilter: filters.companyFilter,
    },
  });

  const declineEmployeeQuestionnaire = useDeclineEmployeeDocumentTemplate({});

  const closeModal = () => {
    setActiveModal(null);
    setCurrentEmployeeData(INITIAL_QUESTIONNAIRE_DATA);
  };

  const onDeleteSubmit = () => {
    declineEmployeeQuestionnaire({
      variables: {
        payload: {
          openQuestionnaireId: currentEmployeeData._id,
          newStatus: EmployeeQuestionnaireStatusesEnum.declined,
          currentMemberId: member?._id,
        },
      },
    });
    closeModal();
  };

  return (
    <div>
      <OpenQuestionnairesTableFilter
        isOpened={activeModal === QUESTIONNAIRE_MODALS.tableFilter}
        onClose={closeModal}
        setFilters={setFilters}
        filters={filters}
      />
      <DeleteModal
        translate={t}
        isOpened={activeModal === QUESTIONNAIRE_MODALS.delete}
        onClose={closeModal}
        submitBtnText={t('common:buttons.delete')}
        title={t('employee:questionnaire.labels.deleteModal.title')}
        subTitle={t('employee:questionnaire.labels.deleteModal.subtitle')}
        onSubmit={onDeleteSubmit}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('employee:labels:employee')}
        </Typography>
        <div>
          <SearchInput
            value={filters.searchParam}
            onChange={e => setFilters({ ...filters, searchParam: e.target.value })}
          />
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.filterAlt}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => setActiveModal(QUESTIONNAIRE_MODALS.tableFilter)}
            >
              {`${t('common:buttons.filterBy')} ${getFiltersCount(filters.companyFilter)}`}
            </Button>
          </ButtonsWrapper>
        </div>
      </HeaderContainer>
      <OpenQuestionnairesListTable
        loading={loading}
        onTableChange={onTableChange}
        dataSource={employees}
        paginationConfig={paginationConfig}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setCurrentEmployeeData(record);
        }}
      />
    </div>
  );
};

export default OpenQuestionnairesList;
